import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { ThunkApi } from '../../store'
import { WithRedirectBack } from '.'
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from './notificationActions'
import {
  AdCreative,
  AdCreativeEventInit,
  AdCreativeInit,
  AdCreativeUpdate,
  AdCreativeWithUrls,
} from 'common/api/v1/types'
import { AdCreativesRequestParams } from '../../api/nm-types'
import { ListResult } from 'common/api/v1/types'
import { withDefaultPagination } from '../../utils'

export const listAdCreatives = createAsyncThunk<ListResult<AdCreativeWithUrls>, AdCreativesRequestParams, ThunkApi>(
  'adCreatives/listAdCreatives',
  async (params, { dispatch, extra: { api } }) => {
    try {
      return await api.adCreativesApi.listAdCreatives(withDefaultPagination(params))
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'fetch Ad Creatives' }))
      throw error
    }
  },
)

export const getAdCreative = createAsyncThunk<AdCreativeWithUrls, string, ThunkApi>(
  'adCreatives/getAdCreative',
  async (id, { dispatch, extra: { api } }) => {
    try {
      return await api.adCreativesApi.getAdCreative(id)
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'fetch Ad Creative' }))
      throw error
    }
  },
)

export const createAdCreative = createAsyncThunk<AdCreativeWithUrls, AdCreativeInit, ThunkApi>(
  'adCreatives/createAdCreative',
  async (adCreativeInit, { dispatch, extra: { api } }) => {
    try {
      const res = await api.adCreativesApi.createAdCreative(adCreativeInit)
      dispatch(enqueueSuccessSnackbar('Ad Creative created'))
      return res
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'create Ad Creative' }))
      throw error
    }
  },
)

export const updateAdCreative = createAsyncThunk<AdCreativeWithUrls, AdCreativeUpdate, ThunkApi>(
  'adCreatives/updateAdCreative',
  async (adCreative, { dispatch, extra: { routes, api, navigate } }) => {
    try {
      const res = await api.adCreativesApi.updateAdCreative(adCreative.id, adCreative)
      navigate()(routes.adCreatives())
      dispatch(enqueueSuccessSnackbar('Ad Creative saved'))
      return res
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'update Ad Creative' }))
      throw error
    }
  },
)

export const deleteAdCreative = createAsyncThunk<AdCreative, { id: string } & WithRedirectBack, ThunkApi>(
  'adCreatives/deleteAdCreative',
  async ({ id, redirect }, { dispatch, extra: { api, routes, navigate } }) => {
    try {
      const res = await api.adCreativesApi.deleteAdCreative(id)
      redirect && navigate()(routes.adCreatives())
      dispatch(enqueueSuccessSnackbar('Ad Creative deleted'))
      return res
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'delete Ad Creative' }))
      throw error
    }
  },
)

export const deleteAdCreatives = createAsyncThunk<string[], string[], ThunkApi>(
  'adCreatives/deleteAdCreatives',
  async (ids, { dispatch, extra: { api } }) => {
    try {
      for (const id of ids) {
        await api.adCreativesApi.deleteAdCreative(id)
      }
      dispatch(enqueueSuccessSnackbar('Ad Creative deleted'))
      return ids
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'delete Ad Creative' }))
      throw error
    }
  },
)

export const createAdCreativeEvent = createAsyncThunk<
  AdCreativeEventInit,
  AdCreativeEventInit & { id: string },
  ThunkApi
>('adCreatives/createAdCreativeEvent', async (adCreativeEventInit, { dispatch, extra: { api } }) => {
  try {
    return await api.adCreativesApi.createAdCreativeEvent(adCreativeEventInit.id, adCreativeEventInit)
  } catch (error) {
    dispatch(enqueueErrorSnackbar({ error, operation: 'create Ad Creative Event' }))
    throw error
  }
})

export const clearAdCreative = createAction('adCreatives/clearAdCreative')
export const clearAdCreatives = createAction('adCreatives/clearAdCreatives')
